import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { message } from "antd";

import {
  selectModalProductGroups,
  selectIsUpdateProductGroup,
  selectProductGroupForUpdate,
  selectProductGroupsRefreshTable,
} from "../../../redux";
import {
  setModalProductGroups,
  setIsUpdateProductGroup,
  setProductGroupForUpdate,
  refreshTableProductGroups,
} from "../../../redux/actions";
import { ProductGroup } from "../../../redux/@types";
import useAppDispatch from "../../../redux/hooks/useAppDispatch";
import { getService } from "../../../clients/feathers.clients";

export const useProductGroups = () => {
  const dispatch = useAppDispatch();

  const modalProductGroups = useSelector(selectModalProductGroups);
  const isUpdateProductGroup = useSelector(selectIsUpdateProductGroup);
  const productGroupForUpdate = useSelector(selectProductGroupForUpdate);
  const isRefreshTableProductGroups = useSelector(
    selectProductGroupsRefreshTable
  );

  const [isLoading, setIsLoading] = useState(false);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);

  const formMethodsCreate = useForm<ProductGroup>({
    defaultValues: {
      name: "",
      description: "",
      active: true,
    },
  });

  const formMethodsUpdate = useForm<ProductGroup>({
    defaultValues: {
      name: "",
      description: "",
      active: true,
    },
  });

  // Reset form when productGroupForUpdate changes
  useEffect(() => {
    if (isUpdateProductGroup && productGroupForUpdate) {
      formMethodsCreate.reset();
      // Convert boolean active to string for InputSelect
      const formData = {
        ...productGroupForUpdate,
        active: productGroupForUpdate.active ? "true" : "false",
      };
      formMethodsUpdate.reset(formData as any);
    }

    return () => {
      formMethodsUpdate.reset();
    };
  }, [isUpdateProductGroup, productGroupForUpdate]);

  // Load product groups
  const loadProductGroups = async () => {
    setIsLoading(true);
    try {
      const response = await getService("product-groups").find({
        query: {
          $limit: 1000,
        },
      });
      setProductGroups(response.data || []);
    } catch (error) {
      console.error("Error loading product groups:", error);
      message.error("Error al cargar los grupos de productos");
    } finally {
      setIsLoading(false);
    }
  };

  // Load product groups on mount
  useEffect(() => {
    loadProductGroups();
  }, []);

  // Refresh table when needed
  useEffect(() => {
    if (isRefreshTableProductGroups) {
      loadProductGroups();
      dispatch(refreshTableProductGroups(false));
    }
  }, [isRefreshTableProductGroups]);

  const changeModalProductGroupsState = () => {
    dispatch(setModalProductGroups(!modalProductGroups));
    if (!modalProductGroups) {
      dispatch(setIsUpdateProductGroup(false));
      dispatch(setProductGroupForUpdate(undefined));
    }
  };

  const createProductGroup = async (data: ProductGroup) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getService("product-groups").create(data);
      setIsLoading(false);
      if (response.id) {
        dispatch(setModalProductGroups(false));
        message.success(
          `Se ha creado con éxito el grupo con id:${response.id}`
        );
        dispatch(refreshTableProductGroups(true));
        formMethodsCreate.reset();
      } else {
        message.error(response as any);
      }
    } catch (error: any) {
      setIsLoading(false);
      console.error(error);
      message.error(error.message || "Error al crear el grupo");
    }
  };

  const updateProductGroup = async (data: ProductGroup) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getService("product-groups").patch(data.id!, data);
      setIsLoading(false);
      if (response.id) {
        dispatch(setModalProductGroups(false));
        message.success(
          `Se ha actualizado con éxito el grupo con id:${response.id}`
        );
        dispatch(refreshTableProductGroups(true));
        formMethodsUpdate.reset();
      } else {
        message.error(response as any);
      }
    } catch (error: any) {
      setIsLoading(false);
      console.error(error);
      message.error(error.message || "Error al actualizar el grupo");
    }
  };

  const deleteProductGroup = async (id: number) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getService("product-groups").remove(id);
      setIsLoading(false);
      if (response) {
        message.success(`Se ha eliminado con éxito el grupo con id:${id}`);
        dispatch(refreshTableProductGroups(true));
      } else {
        message.error(response as any);
      }
    } catch (error: any) {
      setIsLoading(false);
      console.error(error);
      message.error(error.message || "Error al eliminar el grupo");
    }
  };

  const onSubmitCreate = (data: ProductGroup) => {
    // Convert string/number values to boolean for active field and remove products
    const activeValue = (data as any).active;
    const { products, ...dataWithoutProducts } = data;
    const processedData = {
      ...dataWithoutProducts,
      active:
        activeValue === "true" || activeValue === true || activeValue === 1,
    };
    createProductGroup(processedData);
  };

  const onSubmitUpdate = (data: ProductGroup) => {
    // Convert string/number values to boolean for active field and remove products
    const activeValue = (data as any).active;
    const { products, ...dataWithoutProducts } = data;
    const processedData = {
      ...dataWithoutProducts,
      active:
        activeValue === "true" || activeValue === true || activeValue === 1,
    };
    updateProductGroup(processedData);
  };

  return {
    productGroups,
    isLoading,
    modalProductGroups,
    isUpdateProductGroup,
    productGroupForUpdate,
    formMethodsCreate,
    formMethodsUpdate,
    createProductGroup,
    updateProductGroup,
    deleteProductGroup,
    changeModalProductGroupsState,
    onSubmitCreate,
    onSubmitUpdate,
  };
};
